import { useMemo } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckSquare, ChevronRight, AlertTriangle, Clock, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Task } from "@shared/schema";

interface TaskSummary {
  overdueCount: number;
  dueTodayCount: number;
  dueThisWeekCount: number;
  inProgressCount: number;
  totalActive: number;
}

interface MyTasksSummaryWidgetProps {
  tasks: Task[];
  summary?: TaskSummary;
}

export default function MyTasksSummaryWidget({ tasks, summary }: MyTasksSummaryWidgetProps) {
  const [, navigate] = useLocation();

  // Compute from client-side tasks if backend summary not available yet
  const stats = useMemo(() => {
    if (summary) return summary;

    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const weekEnd = new Date(now);
    weekEnd.setDate(weekEnd.getDate() + (7 - weekEnd.getDay()));

    const active = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled' && !t.deletedAt);
    const overdue = active.filter(t => t.dueDate && new Date(t.dueDate) < now && t.dueDate.toString().split('T')[0] !== todayStr);
    const dueToday = active.filter(t => t.dueDate && t.dueDate.toString().split('T')[0] === todayStr);
    const dueThisWeek = active.filter(t => {
      if (!t.dueDate) return false;
      const d = new Date(t.dueDate);
      return d > now && d <= weekEnd;
    });
    const inProgress = active.filter(t => t.status === 'in_progress');

    return {
      overdueCount: overdue.length,
      dueTodayCount: dueToday.length,
      dueThisWeekCount: dueThisWeek.length,
      inProgressCount: inProgress.length,
      totalActive: active.length,
    };
  }, [tasks, summary]);

  const items = [
    { label: 'Overdue', count: stats.overdueCount, color: 'text-rose-600 dark:text-rose-400', bgColor: 'bg-rose-50 dark:bg-rose-950/30', icon: AlertTriangle },
    { label: 'Due Today', count: stats.dueTodayCount, color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-50 dark:bg-amber-950/30', icon: Clock },
    { label: 'This Week', count: stats.dueThisWeekCount, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-50 dark:bg-blue-950/30', icon: CheckSquare },
    { label: 'In Progress', count: stats.inProgressCount, color: 'text-violet-600 dark:text-violet-400', bgColor: 'bg-violet-50 dark:bg-violet-950/30', icon: Loader2 },
  ];

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-md">
              <CheckSquare className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-lg font-bold">My Tasks</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/30 gap-1 font-semibold h-8 px-2"
            onClick={() => navigate("/my-tasks")}
          >
            View All <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {stats.totalActive === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <CheckSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No active tasks</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {items.map(({ label, count, color, bgColor, icon: Icon }) => (
              <div key={label} className={cn("rounded-xl p-3 text-center", bgColor)}>
                <div className={cn("flex items-center justify-center gap-1.5 mb-1", color)}>
                  <Icon className="h-4 w-4" />
                  <span className="text-2xl font-bold">{count}</span>
                </div>
                <span className="text-xs text-muted-foreground font-medium">{label}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
